<div class="project-documents-page">
  <div class="page-header">
    <div>
      <p class="eyebrow">Project Knowledge Hub</p>
      <h2>Documents</h2>
      <p class="subtitle">Browse uploaded files and build a living workspace of folders, internal notes, SOPs, and handover pages.</p>
    </div>
    <div class="header-actions">
      <label class="project-picker">
        <span>Project</span>
        <select [(ngModel)]="selectedProjectId" (ngModelChange)="loadDocuments()">
          <option [ngValue]="null">Select project</option>
          <option *ngFor="let project of projects" [ngValue]="project.id">{{ project.name }}</option>
        </select>
      </label>
      <button mat-stroked-button type="button" (click)="loadProjects()">Refresh</button>
    </div>
  </div>

  <mat-progress-bar *ngIf="loading" mode="indeterminate"></mat-progress-bar>

  <ng-container *ngIf="selectedProject as project; else emptyState">
    <section class="summary-grid" *ngIf="viewMode === 'uploads'">
      <article class="summary-card lead">
        <span>Knowledge Scope</span>
        <strong>{{ project.name }}</strong>
        <small>{{ workspaceDocumentCount }} authored docs • {{ filteredDocuments.length }} uploads</small>
      </article>
      <article class="summary-card">
        <span>Workspace Folders</span>
        <strong>{{ rootFolderCount }}</strong>
      </article>
      <article class="summary-card">
        <span>Workspace Docs</span>
        <strong>{{ workspaceDocumentCount }}</strong>
      </article>
      <article class="summary-card">
        <span>Uploaded Files</span>
        <strong>{{ filteredDocuments.length }}</strong>
      </article>
    </section>

    <section class="mode-switcher">
      <button type="button" [class.active]="viewMode === 'uploads'" (click)="viewMode = 'uploads'">
        <span>Uploads</span>
        <small>Existing files, previews, attachments</small>
      </button>
      <button type="button" [class.active]="viewMode === 'workspace'" (click)="viewMode = 'workspace'">
        <span>Workspace</span>
        <small>Folders and authored pages like an internal wiki</small>
      </button>
    </section>

    <ng-container *ngIf="viewMode === 'uploads'; else workspaceView">
      <section class="workspace-grid">
        <article class="library-panel">
          <div class="panel-head">
            <div>
              <h3>Uploaded Library</h3>
              <p>Project files, onboarding attachments, and task media in one searchable stream.</p>
            </div>
            <input [(ngModel)]="search" (ngModelChange)="onSearchChange()" type="text" placeholder="Search files, source, or owner">
          </div>

          <section class="type-strip">
            <article class="type-chip" *ngFor="let item of typeSummary">
              <span>{{ item.label }}</span>
              <strong>{{ item.value }}</strong>
            </article>
          </section>

          <div class="document-list" *ngIf="filteredDocuments.length; else noDocs">
            <button
              class="document-row"
              type="button"
              *ngFor="let document of filteredDocuments; trackBy: trackByDocumentId"
              [ngClass]="{ active: selectedDocumentId === document.id }"
              (click)="openPreview(document)">
              <div class="doc-type">{{ document.type }}</div>
              <div class="doc-meta">
                <strong>{{ document.title }}</strong>
                <span>{{ document.source }}</span>
                <small>{{ document.uploadedBy }} • {{ document.uploadedAt | date:'mediumDate' }}</small>
              </div>
            </button>
          </div>
        </article>

        <article class="preview-panel" *ngIf="displayedDocument as document">
          <div class="panel-head">
            <div>
              <h3>{{ document.title }}</h3>
              <p>{{ document.source }} • {{ document.fileName }}</p>
            </div>
            <a mat-stroked-button [href]="document.url" target="_blank" rel="noopener">Open in New Tab</a>
          </div>

          <div class="preview-shell">
            <ng-container [ngSwitch]="mediaKind(document)">
              <img *ngSwitchCase="'image'" [src]="document.url" [alt]="document.title">
              <video *ngSwitchCase="'video'" [src]="document.url" controls></video>
              <audio *ngSwitchCase="'audio'" [src]="document.url" controls></audio>
              <iframe *ngSwitchCase="'pdf'" [src]="pdfPreviewUrl" title="Document preview"></iframe>
              <div class="fallback" *ngSwitchDefault>
                <mat-icon>insert_drive_file</mat-icon>
                <p>Preview is not available for this file type.</p>
                <a [href]="document.url" target="_blank" rel="noopener">Open file</a>
              </div>
            </ng-container>
          </div>

          <div class="preview-meta">
            <div>
              <span>Type</span>
              <strong>{{ document.type }}</strong>
            </div>
            <div>
              <span>Uploaded By</span>
              <strong>{{ document.uploadedBy }}</strong>
            </div>
            <div>
              <span>Date</span>
              <strong>{{ document.uploadedAt | date:'medium' }}</strong>
            </div>
          </div>
        </article>
      </section>
    </ng-container>

    <ng-template #workspaceView>
      <section class="workspace-mode-grid immersive">
        <article class="workspace-nav">
          <div class="panel-head workspace-nav-head">
            <div class="workspace-nav-title">
              <h3>Pages & Folders</h3>
            </div>
            <div class="workspace-search-wrap">
              <input [(ngModel)]="workspaceSearch" type="text" placeholder="Search folder or page">
            </div>
          </div>

          <div class="workspace-actions">
            <button mat-stroked-button type="button" (click)="openCreateFolderModal()">New Folder</button>
            <button mat-flat-button color="primary" type="button" (click)="openCreateDocumentModal()">New Document</button>
          </div>

          <div class="breadcrumb-strip">
            <button type="button" class="crumb root" [class.active]="currentFolderId === null" (click)="navigateToRoot()">Root</button>
            <ng-container *ngFor="let crumb of folderBreadcrumbs">
              <span>/</span>
              <button type="button" class="crumb" [class.active]="crumb.id === currentFolderId" (click)="navigateToFolder(crumb.id)">{{ crumb.name }}</button>
            </ng-container>
          </div>

          <div class="workspace-list" *ngIf="visibleFolders.length || visibleKnowledgeDocuments.length; else noWorkspaceContent">
            <div class="folder-tree-node" *ngFor="let folder of visibleFolders; trackBy: trackByFolderId">
              <div class="workspace-item folder" (click)="openFolder(folder)">
                <div class="folder-row">
                  <button
                    *ngIf="folderHasFiles(folder.id) || folderHasChildren(folder.id)"
                    type="button"
                    class="folder-toggle"
                    (click)="toggleFolder(folder, $event)"
                    [attr.aria-label]="isFolderExpanded(folder.id) ? 'Collapse folder' : 'Expand folder'">
                    <mat-icon>{{ isFolderExpanded(folder.id) ? 'expand_more' : 'chevron_right' }}</mat-icon>
                  </button>
                  <span class="folder-spacer" *ngIf="!folderHasFiles(folder.id) && !folderHasChildren(folder.id)"></span>
                  <strong>{{ folder.name }}</strong>
                </div>
              </div>

              <div class="folder-files" *ngIf="isFolderExpanded(folder.id)">
                <button
                  type="button"
                  class="workspace-item document nested-file"
                  *ngFor="let document of folderContents(folder.id); trackBy: trackByKnowledgeDocumentId"
                  [class.active]="selectedKnowledgeDocumentId === document.id"
                  (click)="openKnowledgeDocument(document)">
                  <div class="workspace-copy">
                    <strong>{{ document.title }}</strong>
                  </div>
                </button>
              </div>
            </div>

            <button
              type="button"
              class="workspace-item document list-row"
              *ngFor="let document of visibleKnowledgeDocuments; trackBy: trackByKnowledgeDocumentId"
              [class.active]="selectedKnowledgeDocumentId === document.id"
              (click)="openKnowledgeDocument(document)">
              <div class="workspace-copy">
                <strong>{{ document.title }}</strong>
              </div>
            </button>
          </div>
        </article>

        <article class="workspace-editor" *ngIf="activeKnowledgeDocument as document; else workspaceIntro">
          <div class="editor-head">
            <div>
              <p class="eyebrow">Knowledge Page</p>
              <h3>{{ document.title }}</h3>
              <p>Created by {{ document.createdByName || 'Team member' }} • Updated {{ document.updatedAt | date:'medium' }}</p>
            </div>
            <div class="editor-actions">
              <button mat-stroked-button type="button" (click)="openCreateDocumentModal()">New Page</button>
              <button mat-flat-button color="primary" type="button" [disabled]="savingWorkspace" (click)="saveKnowledgeDocument()">
                {{ savingWorkspace ? 'Saving...' : 'Save Changes' }}
              </button>
            </div>
          </div>

          <section class="editor-form">
            <div class="editor-grid">
              <label>
                <span>Document Title</span>
                <input [(ngModel)]="editorModel.title" type="text">
              </label>
              <label>
                <span>File Type</span>
                <select [(ngModel)]="editorModel.documentType">
                  <option value="Document">Document</option>
                  <option value="Sheet">Sheet</option>
                </select>
              </label>
              <label>
                <span>Status</span>
                <select [(ngModel)]="editorModel.status">
                  <option value="Draft">Draft</option>
                  <option value="In Review">In Review</option>
                  <option value="Published">Published</option>
                  <option value="Archived">Archived</option>
                </select>
              </label>
              <label>
                <span>Folder</span>
                <select [(ngModel)]="editorModel.folderId">
                  <option [ngValue]="null">Root workspace</option>
                  <option *ngFor="let folder of folders" [ngValue]="folder.id">{{ folder.name }}</option>
                </select>
              </label>
            </div>

            <label class="stacked">
              <span>Content</span>
              <div class="editor-toolbar" role="toolbar" aria-label="Document formatting">
                <button type="button" (click)="applyBlock('P')">Text</button>
                <button type="button" (click)="applyBlock('H1')">H1</button>
                <button type="button" (click)="applyBlock('H2')">H2</button>
                <button type="button" (click)="applyEditorCommand('bold')"><strong>B</strong></button>
                <button type="button" (click)="applyEditorCommand('italic')"><em>I</em></button>
                <button type="button" (click)="applyEditorCommand('underline')"><u>U</u></button>
                <button type="button" (click)="applyEditorCommand('insertUnorderedList')">Bullets</button>
                <button type="button" (click)="applyEditorCommand('insertOrderedList')">Numbered</button>
                <button type="button" [matMenuTriggerFor]="tableToolsMenu">Table Tools</button>
                <button type="button" (click)="applyBlock('BLOCKQUOTE')">Quote</button>
                <button type="button" (click)="clearEditorFormatting()">Clear</button>
              </div>

              <mat-menu #tableToolsMenu="matMenu" class="table-tools-menu">
                <button mat-menu-item type="button" (click)="insertTable()">Insert Table</button>
                <button mat-menu-item type="button" (click)="addTableRow()">Add Row</button>
                <button mat-menu-item type="button" (click)="addTableColumn()">Add Column</button>
                <button mat-menu-item type="button" (click)="deleteTableRow()">Delete Row</button>
                <button mat-menu-item type="button" (click)="deleteTableColumn()">Delete Column</button>
                <button mat-menu-item type="button" (click)="deleteSelectedTable()">Delete Table</button>
              </mat-menu>

              <div
                #documentEditor
                class="content-editor document-surface"
                contenteditable="true"
                spellcheck="true"
                (input)="onEditorInput()"></div>
            </label>

            <section class="details-strip">
              <article>
                <span>Type</span>
                <strong>{{ document.documentType || 'Document' }}</strong>
              </article>
              <article>
                <span>Created By</span>
                <strong>{{ document.createdByName || 'Team member' }}</strong>
              </article>
              <article>
                <span>Updated By</span>
                <strong>{{ document.updatedByName || document.createdByName || 'Team member' }}</strong>
              </article>
              <article>
                <span>Last Updated</span>
                <strong>{{ document.updatedAt | date:'medium' }}</strong>
              </article>
            </section>
          </section>
        </article>
      </section>
    </ng-template>
  </ng-container>
</div>

<ng-template #emptyState>
  <section class="empty-state">
    <mat-icon>folder_open</mat-icon>
    <h3>No project selected</h3>
    <p>Select a project to browse uploads or build a structured knowledge workspace.</p>
  </section>
</ng-template>

<ng-template #noDocs>
  <div class="library-empty">
    <mat-icon>description</mat-icon>
    <h3>No uploaded files found</h3>
    <p>Project files, task attachments, and onboarding media will appear here.</p>
  </div>
</ng-template>

<ng-template #workspaceIntro>
  <article class="workspace-editor intro">
    <mat-icon>edit_note</mat-icon>
    <h3>No document selected</h3>
    <p>Create a document or open an existing one to edit content, update status, and structure project knowledge.</p>
  </article>
</ng-template>

<ng-template #noWorkspaceContent>
  <div class="library-empty">
    <mat-icon>folder_copy</mat-icon>
    <h3>Workspace is empty</h3>
    <p>Create your first folder or document to start organizing this project like a living knowledge base.</p>
  </div>
</ng-template>
